import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { RequireAuth } from '@/components/auth/RequireAuth';
import { RequireCapture } from '@/components/capture/RequireCapture';
import { SignIn } from '@/routes/SignIn';
import { MyJobs } from '@/routes/MyJobs';
import { JobView } from '@/routes/JobView';
import { CaptureEntry } from '@/routes/CaptureEntry';
import { CaptureViewfinder } from '@/routes/CaptureViewfinder';
import { CaptureWorkflowPicker } from '@/routes/CaptureWorkflowPicker';
import { QualityRetake } from '@/routes/QualityRetake';
import { ExtractionResult } from '@/routes/ExtractionResult';
import { RapidSession } from '@/routes/RapidSession';
import { OfflineQueue } from '@/routes/OfflineQueue';
import { FlaggedCaptures } from '@/routes/FlaggedCaptures';

/**
 * The route table. CA-01 (sign in) sits outside the shell; every other
 * screen renders inside it (wireframes/capture-app.html CA-01 to CA-08).
 *
 * Home is My jobs (CAPTURE_JOBS.md section 6). There is no capture without
 * a job, so every route that builds toward the shutter sits under
 * `/jobs/:jobId/capture` and carries the job in its path: the viewfinder,
 * the workflow picker, the quality-gate retake and rapid session. `/capture`
 * is the Capture tab, which opens the chosen job's viewfinder or My jobs.
 *
 * `RequireAuth` wraps the shell rather than each screen, so a route added
 * to the shell later is gated by construction. `RequireCapture`
 * (OPERATOR_ACCESS.md section 5) wraps every capture route. The result
 * screen sits outside it, because opening a capture already taken needs no
 * capture right.
 */
export const router = createBrowserRouter([
  { path: '/', element: <Navigate to="/sign-in" replace /> },
  { path: '/sign-in', element: <SignIn /> }, // CA-01
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: '/work', element: <MyJobs /> }, // CA-02, My jobs
          { path: '/jobs/:jobId', element: <JobView /> },
          { path: '/capture/:captureId/result', element: <ExtractionResult /> }, // CA-05
          { path: '/queue', element: <OfflineQueue /> }, // CA-07
          { path: '/flagged', element: <FlaggedCaptures /> }, // CA-08
          {
            element: <RequireCapture />,
            children: [
              { path: '/capture', element: <CaptureEntry /> },
              { path: '/jobs/:jobId/capture', element: <CaptureViewfinder /> }, // CA-03
              { path: '/jobs/:jobId/capture/workflow', element: <CaptureWorkflowPicker /> },
              { path: '/jobs/:jobId/capture/retake', element: <QualityRetake /> }, // CA-04
              { path: '/jobs/:jobId/capture/rapid', element: <RapidSession /> }, // CA-06
            ],
          },
        ],
      },
    ],
  },
]);
